import { ReactNode } from 'react';
import clsx from 'clsx';
import { HugeiconsIcon, IconSvgElement } from '@hugeicons/react';
import { ClickeableColor } from '../types/colors';

interface NActionIconProps {
  children?: ReactNode;
  onClick?: () => void;
  size?:  'xs'| 'small' | 'medium' | 'large';
  color?: ClickeableColor;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  hugeIcon: IconSvgElement;
  strokeWidth?: number;
}

export const NActionIcon = ({
                              children,
                              onClick,
                              size = 'medium',
                              color = 'primary',
                              disabled = false,
                              loading = false,
                              className,
                              hugeIcon,
                              strokeWidth = 2
                            }: NActionIconProps) => {
  const sizeClasses = {
    xs: 'p-1 text-xs rounded-full',
    small: 'p-2 text-sm rounded-full',
    medium: 'p-3 text-sm rounded-full',
    large: 'p-4 text-lg rounded-full'
  }[size];

  const colorClasses = {
    primary: 'bg-primary text-black hover:bg-primary/80',
    secondary: 'bg-neutral-900 text-white hover:bg-neutral-800',
    tertiary: 'bg-slate-200 text-slate-900 hover:bg-slate-300',
    outline: 'text-slate-900 hover:bg-slate-100 ring ring-slate-300',
    transparent: 'bg-transparent text-black hover:bg-slate-100',
    link: 'text-info-600 hover:text-info-700 hover:bg-info-50',
    danger: 'bg-error-600 text-white hover:bg-error-700',
    dangerOutline: 'text-error-600 hover:bg-error-100 hover:text-error-700 ring ring-error-300',
    dangerTransparent: 'text-error-600 hover:bg-error-100 hover:text-error-700'
  }[color];

  const loadingClasses = {
    primary: 'bg-primary-loading',
    secondary: 'bg-secondary-loading',
    tertiary: 'bg-tertiary-loading',
    outline: 'bg-tertiary-loading',
    transparent: 'bg-tertiary-loading',
    link: 'bg-info-loading',
    danger: 'bg-error-loading',
    dangerOutline: 'bg-error-loading',
    dangerTransparent: 'bg-error-loading'
  }[color];

  const iconSize = {
    xs: 16,
    small: 20,
    medium: 20,
    large: 20
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex cursor-pointer relative items-center justify-center font-medium transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none grow-0 shrink-0 aspect-square',
        sizeClasses,
        colorClasses,
        className
      )}
    >
      {hugeIcon &&
        <HugeiconsIcon size={iconSize[size]} strokeWidth={strokeWidth} icon={hugeIcon} />
      }
      {loading && (
        <div className={clsx('absolute w-full h-full top-0', loadingClasses)} />
      )}
    </button>
  );
};
